/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import HsStyleProvider from 'hellosign/common/style/hs-style-provider';
import { BrowserRouter } from 'react-router-dom';
import HSIntlProvider from 'hellospa/components/hs-intl-provider';
import { FeatureFlags, FeatureFlagProvider } from 'hellospa/components/feature-flags';
import { setCsrfToken } from 'hellospa/hs-fetch';
import { HSUserProvider } from 'hellospa/common/hs-user';
import { GlobalPageProps, setPageProps } from 'hellospa/slices/page-props';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'hellospa/redux/types';
import { TourContextProvider } from './tour';
import { CodeLine } from 'dig-components/dist/icons/src';
import { DebugPanel } from 'hello-react/components/debug-panel';

const AppContext = React.lazy(() => import('hellospa/app-context'));
const StoreProvider = React.lazy(() => import('hellospa/redux/store'));

const pathRegex = /\/([^/]+\.php)?/;
const defaultFallback = (
  // This should either be empty, or be something production ready because the
  // Home is also lazy loaded.
  <div />
);
type SPAProps = {
  children: React.ReactNode,
  suspenseFallback?: React.ReactNode,
  featureFlags: FeatureFlags,
  csrfToken: string,
  csrfTokenAttachmentDelete: string,
  initialUser?: {
    primarySignatureGuid: string,
    isFree: boolean,
  }
  preloadedTsmGroupKey: string,
  tours?: string[]
  pageProps?: GlobalPageProps
}
export default function SinglePageApp({
  children,
  featureFlags,
  csrfToken,
  initialUser,
  preloadedTsmGroupKey,
  csrfTokenAttachmentDelete,
  suspenseFallback = defaultFallback,
  pageProps,
  tours = [],
}: SPAProps) {
  setCsrfToken(csrfToken);
  // In development you can access a production-like environment through
  // `/webapp_prod.php`. This assumes if your path starts with a PHP file,
  // then it should be the base for routing.
  const [basename] = window.location.pathname.match(pathRegex) || [undefined];
  return (
    <React.Suspense fallback={suspenseFallback}>
      <HsStyleProvider>
        <BrowserRouter basename={basename}>
          <HSUserProvider value={initialUser as any}>
            <AppContext
              initialUser={initialUser}
              csrfToken={csrfToken}
              csrfTokenAttachmentDelete={csrfTokenAttachmentDelete}
              preloadedTsmGroupKey={preloadedTsmGroupKey}
            >
              <HSIntlProvider>
                <FeatureFlagProvider featureFlags={featureFlags || {}}>
                  <StoreProvider>
                    <CapturePageProps pageProps={pageProps} />
                    <TourContextProvider value={tours}>
                      {children}
                    </TourContextProvider>
                  </StoreProvider>
                </FeatureFlagProvider>
              </HSIntlProvider>
            </AppContext>
          </HSUserProvider>
        </BrowserRouter>
      </HsStyleProvider>
    </React.Suspense>
  );
}

function CapturePageProps({ pageProps }: { pageProps?: GlobalPageProps }) {
  const dispatch = useDispatch<Dispatch>();
  React.useEffect(() => {
    if (pageProps) {
      dispatch(setPageProps(pageProps));
    }
  }, [dispatch, pageProps]);

  const PagePropsPanel = () => {
    return (
      <React.Fragment>
        <DebugPanel.SectionHeader>
          PageProps
        </DebugPanel.SectionHeader>
        <pre>
          {JSON.stringify(pageProps, null, 2)}
        </pre>
      </React.Fragment>
    );
  };

  return <DebugPanel title="PageProps" icon={CodeLine} Component={PagePropsPanel} />;
}
